import React, { useEffect, useState } from 'react'
import { useI18n } from '@ggs/i18n'
import { get, uniqBy, isArray } from 'lodash'
import { useAcademicApi } from '../../../local'
import { Typography } from '@mui/material'
import { CourseSectionsList } from '../../'
import { Loader } from '../../loader'
import { useSelector } from '@ggs/store'
import './CourseView.scss'

/**
 * @typedef {import('../../../definitions').CourseSectionExpRecord} CourseSectionExpRecord
 * @typedef {import('../../../definitions').CourseRecord} CourseRecord
 */

/**
 * @typedef {Object} CourseViewProps
 */

/**
 * Specific course detailed view.
 * @return {JSX.Element}
 */
const CourseView = () => {
  const { t } = useI18n()
  const { getCourse, getCourseSections } = useAcademicApi()
  const courseId = get(window, 'drupalSettings.react.courseId', 'COURSE_DEFINITION-3-15899')
  const courseInstanceId = get(
    window,
    'drupalSettings.react.courseInstanceId',
    'COURSE_DEFINITION-3-963_20240901'
  )
  const academicPeriod = useSelector((state) => state.settings.academicPeriod)
  const pageSize = 500

  /**
   * @type {CourseRecord}
   */
  const defaultCourse = null
  const [course, setCourse] = useState(defaultCourse)

  /**
   * @type {CourseSectionExpRecord[]}
   */
  const defaultCourseSections = null
  const [courseSections, setCourseSections] = useState(defaultCourseSections)
  const [loading, setLoading] = useState(true)

  /**
   * Resolve course data.
   * @return {Promise<CourseRecord>}
   */
  const resolveCourseData = async () => {
    if (course) {
      return course
    }

    const courseRecord = await getCourse(courseInstanceId)
    setCourse(courseRecord)
    return courseRecord
  }

  /**
   * Resolve course sections data.
   * @return {Promise<void>}
   */
  const resolveCourseSectionsData = async () => {
    setLoading(true)
    const courseRecord = await resolveCourseData()
    if (courseRecord) {
      const courseId = courseRecord.courseId || ''
      const courseSectionsResponse = await getCourseSections({
        pageSize,
        courseId: [courseId],
        academicPeriodId: [academicPeriod],
      })

      const courseSections = courseSectionsResponse?.pageItems
      if (isArray(courseSections)) {
        const augmentedCourseSections = courseSections.map((courseSection) => {
          return {
            ...courseSection,
            specialTopic: get(courseSection, 'specialTopic', ''),
            course: {
              courseNumber: courseRecord.courseNumber,
              title: courseRecord.title,
              abbreviatedTitle: courseRecord.abbreviatedTitle,
              courseSubject: courseRecord.courseSubject,
            },
          }
        })
        augmentedCourseSections.sort((a,b) => 
          a.sectionNumber.localeCompare(b.sectionNumber)
        )
        setCourseSections(augmentedCourseSections)
      }
      setLoading(false)
    }
  }

  useEffect(() => {
    if (academicPeriod) {
      resolveCourseSectionsData().then(() => {})
    }
  }, [academicPeriod])

  if (loading) {
    return <Loader />
  }

  const tOptions = { nsSeparator: '' }

  return (
    <div className="course-view">
      <div
        className="course-view__description"
        dangerouslySetInnerHTML={{ __html: course.description || '' }}
      />
      <div className="course-view__details">
        <div className="course-view__detail-item">
          <Typography variant={'h5'} className="course-view__detail-title">
            {t('Credits: {{credits}}', {
              ...tOptions,
              credits: `${course.minimumUnits} - ${course.maximumUnits}`,
            })}
          </Typography>
        </div>
        {/* <div className="course-view__detail-item">
          <Typography variant="h5" className="course-view__detail-title">
            {t('Co-requisites:', tOptions)}
          </Typography>
          <Typography>
            {uniqBy(
              get(course, 'coRequisiteCourses', [{ description: '' }]),
              'description'
            ).map(({ description }, i) => (
              <div key={i}>{description}</div>
            ))}
          </Typography>
        </div>
        <div className="course-view__detail-item">
          <Typography variant="h5" className="course-view__detail-title">
            {t('Prerequisites:', tOptions)}
          </Typography>
          <Typography>{get(course, 'prerequisite', '')}</Typography>
        </div> */}
        {/* <div className="course-view__detail-item">
          <Typography variant="h5" className="course-view__detail-title">
            {t('Equivalent:', tOptions)}
          </Typography>
          <Typography>
            {uniqBy(
              get(course, 'equivalentCourses', []).filter(({ code }) => code !== courseId),
              'description'
            ).map(({ description }, i) => (
              <div key={i}>{description}</div>
            ))}
          </Typography>
        </div> */}
      </div>
      {courseSections.length > 0 ? (
        <div className="course-view__tables">
          <CourseSectionsList
            courseSections={courseSections}
            sectionViewBasePath="/browse-courses/course-section"
          />
        </div>
      ) : (
        <div className="course-view__no-courses-message">
          <h3>{t('No course sections offered this period.')}</h3>
        </div>
      )}
    </div>
  )
}

export default CourseView
